import { Component, computed, EventEmitter, HostListener, input, Input, Output, TemplateRef } from '@angular/core';
import { Observable } from 'rxjs';
import { MuiI18nProvider } from '../i18n';
import { MuiIconFont } from '../icon';
import { MuiColor } from '../utility';


export enum MuiChipAppearance {
  DEFAULT = 'default',
  OUTLINE = 'outline'
}

@Component({
  selector: 'mui-chip',
  template: `
    @if (customIconTemplate(); as template) {
      <ng-container [ngTemplateOutlet]="template"></ng-container>
    } @else {
      @if (icon(); as icon) {
        <i
          mui-icon
          [font]="iconFont()"
          [name]="icon"
          class="mui-chip__icon"
          aria-hidden="true"
          data-testid="mui-chip-icon"></i>
      }
    }
    <span class="mui-chip__label">
      <ng-content></ng-content>
    </span>
    <button
      *ngIf="canBeDismissed"
      (click)="dismiss.emit()"
      class="mui-chip__dismiss-btn"
      [attr.aria-label]="dismissButtonLabel() ?? (translations$ | async)?.dismissButton"
      tabindex="-1">
      <i mui-icon font="fa" name="times"></i>
    </button>
  `,
  host: {
    /* eslint-disable  @typescript-eslint/naming-convention */
    '[attr.tabindex]': 'dismissible ? 0 : null',
    '[attr.role]': 'dismissible ? "button" : null',
    '[class]': 'classes()'
    /* eslint-enable  @typescript-eslint/naming-convention */
  }
})
export class MuiChip {
  @Input()
  get dismissible(): boolean {
    return this.canBeDismissed;
  }

  /** @deprecated The chip is now interpreted as dismissible when the dismiss event has listeners. */
  set dismissible(_: boolean) {/* noop */}

  readonly appearance = input<MuiChipAppearance | string>(MuiChipAppearance.DEFAULT);
  readonly color = input<MuiColor | string>(MuiColor.DEFAULT);
  readonly icon = input<string | null>(null);
  readonly iconFont = input<MuiIconFont | string>(MuiIconFont.FONTAWESOME);
  readonly customIconTemplate = input<TemplateRef<any> | null>(null);
  readonly dismissButtonLabel = input<string>();

  @Output() dismiss = new EventEmitter<void>();

  protected readonly classes = computed(() => {
    const appearance = this.appearance();
    const classes = [
      'mui-chip',
      `mui-chip--${this.color()}`
    ];

    if (appearance !== MuiChipAppearance.DEFAULT) {
      classes.push(`mui-chip--${appearance}`);
    }

    return classes.join(' ');
  });

  get canBeDismissed(): boolean {
    return this.dismiss.observed;
  }

  protected translations$: Observable<{ dismissButton: string }>;

  constructor(private _i18n: MuiI18nProvider) {
    this.translations$ = this._i18n.chip();
  }

  @HostListener('keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    if (this.dismissible && (event.key === 'Delete' || event.key === 'Backspace')) {
      this.dismiss.emit();
    }
  }
}
